import React, { useState, useEffect, useContext, useRef } from 'react';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faEyeSlash,
  faUserCheck,
} from '@fortawesome/free-solid-svg-icons';
// import core components
import { domHelper, localstorageHelper } from 'helpers';
import { UserContext } from 'contexts';
import { cognitoService } from 'services';

import {
  Wrapper,
  CloseModalButton,
  SocialFieldSet,
  SocialField,
  LoginForm,
  FormTitle,
  NormalFieldSet,
  Field,
  Button,
  ButtonOutline,
  Subtitle,
  Section,
} from '../Login/styles';

// Import static icons
import CloseIcon from '/static/icons/controls/close.svg';
import GoogleIcon from '/static/icons/auth/google.svg';
import FacebookIcon from '/static/icons/auth/facebook.svg';
import AppleIcon from '/static/icons/auth/apple.svg';
import LoadingGif from '/static/gifs/loading.gif';
import PasswordIcon from '/static/icons/auth/password.svg';
import UserIcon from '/static/icons/auth/user.svg';
import { Auth } from 'aws-amplify';
import { socialProviders } from '../../constants';

const RegisterForm = styled(LoginForm)``;

const AcceptToS = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  margin-bottom: 20px;

  @media all and (max-width: ${props => props.theme.breakpoints.s}) {
    font-size: ${props => props.theme.textSize.s};
  }

  input[type='checkbox'] {
    width: 20px;
    height: 20px;
    border-radius: 2px;
    margin-right: 10px;
    border-color: ${props => props.theme.colors.stroke};
  }
`;

const Register = ({ data, setData, onClose, onLogin }) => {
  const { email, password, authCode } = data.user;
  const { error, signupStage, bLoading } = data.meta;
  const [bShowPassword, setBShowPassword] = useState(false); // boolean value to show/hide password
  const [checkToS, setCheckToS] = useState(false);

  const checkToSRef = useRef(null);
  const { setUser, createNotification } = useContext(UserContext); // Context API to update user state
  const handleUserChange = e =>
    setUserData({ [e.target.name]: e.target.value });
  const setUserData = newData => {
    setData({ ...data, user: { ...data.user, ...newData } });
  };
  const setMetaData = newData => {
    setData({ ...data, meta: { ...data.meta, ...newData } });
  };
  const updateData = newData => {
    setData({
      meta: { ...data.meta, ...newData.meta },
      user: { ...data.user, ...newData.user },
    });
  };
  const handleFormSubmit = async ev => {
    // handler when a user submits the form
    ev.preventDefault();
    setMetaData({ error: '' });
    try {
      if (signupStage === 0) {
        // if(password !== confirmPassword) setError('Passwords do not match');
        // else {
        if (!checkToS)
          throw { message: 'You should accept the Terms of Service!' };
        setMetaData({ bLoading: true });
        await cognitoService.signUp({
          username: email,
          password,
          attributes: { email },
        });
        setMetaData({
          bLoading: false,
          signupStage: 1,
          error: 'Confirmation code is sent',
        });
        // }
      } else if (signupStage === 1) {
        setMetaData({ bLoading: true });
        await cognitoService.confirmSignUp(email, authCode);
        createNotification('success', "You're registered successfully");
        const user = await cognitoService.signIn(email, password, true);
        localstorageHelper.saveUser(user);
        setUser(user);
        setMetaData({ signupStage: 0 });
        onClose();
      }
    } catch (err) {
      ////console.log('error registering', err);
      setMetaData({ bLoading: false, error: err.message });
    }
  };
  const handleSocialSignup = async provider => {
    // handler for federated signup
    if (!checkToS) {
      setMetaData({ error: 'You should accept the Terms of Service!' });
      return;
    }
    try {
      const {
        data,
        tab,
        isBrowserClosed,
      } = await cognitoService.federatedSignIn(provider);
      if (data) {
        const cognitoUser = await Auth.currentAuthenticatedUser();
        const user =  await cognitoService.checkUser("",cognitoUser.attributes.email);
        setUser(user);
      }
      tab.close();
      if (!isBrowserClosed) {
        onClose();
      }
      onClose();
    } catch (err) {
      ////console.log('error loggingin', error);
      setMetaData({ error: err.message });
    }
  };
  const toggleCheckToS = ev => {
    checkToSRef.current.checked = !checkToS;
    setCheckToS(!checkToS);
  };
  const changePasswordType = ev => {
    domHelper.changeProperty(
      'register-password',
      'type',
      !bShowPassword ? 'text' : 'password'
    );
    setBShowPassword(!bShowPassword);
  };
  const handleResendCode = async ev => {
    try {
      await cognitoService.resendSignupCode(email);
      setMetaData({ error: 'Confirmation code is resent' });
    } catch (err) {
      ////console.log('error resending code', error);
      setMetaData({ error: err.message });
    }
  };
  const reset = ev => {
    updateData({
      user: { authCode: '' },
      meta: { bLoading: false, error: '' },
    });
    setCheckToS(false);
  };
  useEffect(() => {
    reset();
  }, [signupStage]);

  return (
    <Wrapper>
      {bLoading && (
        <div className="loading-wrapper">
          <img
            className="loading-gif large"
            src={LoadingGif}
            alt="image loading"
          />
        </div>
      )}
      <CloseModalButton onClick={onClose}>
        <CloseIcon />
      </CloseModalButton>
      <RegisterForm onSubmit={handleFormSubmit}>
        <FormTitle>
          {signupStage === 0 ? 'Registration' : 'Confirm email'}
        </FormTitle>
        {error && <p className="error">{error}</p>}
        {signupStage === 0 && (
          <>
            <NormalFieldSet className={bLoading ? 'no-action' : ''}>
              <Field>
                <span>
                  <UserIcon />
                </span>
                <input
                  type="email"
                  required
                  placeholder="Email"
                  name="email"
                  value={email}
                  onChange={handleUserChange}
                />
              </Field>
              <Field>
                <span>
                  <PasswordIcon />
                </span>
                <input
                  type="password"
                  placeholder="Password"
                  id="register-password"
                  required
                  name="password"
                  value={password}
                  onChange={handleUserChange}
                />
                <FontAwesomeIcon
                  icon={bShowPassword ? faEyeSlash : faEye}
                  onClick={changePasswordType}
                />
              </Field>
              <AcceptToS onClick={toggleCheckToS}>
                <input type="checkbox" ref={checkToSRef} />
                <label>I accept the Simply Say Terms of Service</label>
              </AcceptToS>
              <Button type="submit" value="Confirm" />
            </NormalFieldSet>
            <SocialFieldSet>
              <SocialField className="google">
                <span>
                  <GoogleIcon />
                </span>
                <label
                  name="Google"
                  onClick={() => handleSocialSignup(socialProviders.GOOGLE)}
                >
                  Sign up with Google
                </label>
              </SocialField>
              <SocialField className={'facebook'}>
                <span>
                  <FacebookIcon />
                </span>
                <label
                  name="Facebook"
                  onClick={() => handleSocialSignup(socialProviders.FACEBOOK)}
                >
                  Sign up with Facebook
                </label>
              </SocialField>
              <SocialField className="apple" name="Apple">
                <span>
                  <AppleIcon />
                </span>
                <label
                    name="Apple"
                    onClick={() => handleSocialSignup(socialProviders.APPLE)}
                >
                  Sign up with Apple
                </label>
              </SocialField>
            </SocialFieldSet>
            <Section>
              <Subtitle onClick={onLogin} >Already a registered user?</Subtitle>
            </Section>
          </>
        )}
        {signupStage === 1 && (
          <NormalFieldSet>
            <Field>
              <span>
                <FontAwesomeIcon icon={faUserCheck} />
              </span>
              <input
                type="number"
                required
                placeholder="Code"
                name="authCode"
                value={authCode}
                onChange={handleUserChange}
              />
            </Field>
            <Button type="submit" value="Confirm" />
            <Section className="helper-section">
              <span>
                <Subtitle>Didn't received a code?</Subtitle>
                <Subtitle className="action" onClick={handleResendCode}>
                  resend
                </Subtitle>
              </span>
              <span>
                <Subtitle>Register a new account?</Subtitle>
                <Subtitle
                  className="action"
                  onClick={ev => setMetaData({ signupStage: 0 })}
                >
                  register
                </Subtitle>
              </span>
            </Section>
          </NormalFieldSet>
        )}
      </RegisterForm>
    </Wrapper>
  );
};

export default Register;
